import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { AlertBox } from '@/common/AlertBox';
import { PendingEmailNotice } from '@/settings/PendingEmailNotice';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/** Which dismiss flash is showing, and the tab it was raised on. */
interface Flash {
  kind: 'cancelled' | 'cancel-failed';
  path: string;
}

export const Route = createFileRoute('/_app/_users/members/$memberId/settings')(
  {
    component: MemberSettingsLayout,
  },
);

/**
 * The guard for every `/members/{id}/settings/*` screen, and the one place the
 * pending-email-change banner renders.
 *
 * Self-only, narrower than legacy: legacy let other editors open a member's
 * Login Information form, but its write checks the displayed member's password,
 * so all it could do was disclose their email. The sidebar links Settings only
 * for the viewer's own account. A refused viewer is redirected to the member
 * page, as in legacy. It navigates from the component, since the answer depends
 * on `getCurrentUser`.
 *
 * The banner lives on the layout because legacy shows it on both Login
 * Information and Email Preferences. Unlike legacy it also shows on Export
 * Data.
 */
function MemberSettingsLayout(): ReactElement | null {
  const { memberId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  const { pathname } = useLocation();
  // One-shot, and it has to be explicit because nothing here unmounts: legacy's
  // dismiss flash renders once and can't follow the visitor to another tab, but
  // switching tabs keeps this layout mounted. So the flash carries the path it
  // was raised on and is dropped on the first navigation away; coming back
  // doesn't bring it back.
  const [flash, setFlash] = useState<Flash | null>(null);
  const { mutateAsync: cancelEmailChange, isPending: isCancelling } =
    api.cancelMyEmailChange.useMutation();

  useEffect(() => {
    if (flash !== null && flash.path !== pathname) setFlash(null);
  }, [pathname, flash]);

  const refused = me !== undefined && me.id.toString() !== memberId;

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/members/$memberId/',
        params: { memberId },
        replace: true,
      });
    }
  }, [refused, memberId, navigate]);

  // Nothing renders until the viewer is known: the leaves below read the
  // account email out of `me`, so painting them first would flash another
  // member's settings form at a viewer this guard is about to bounce.
  if (me === undefined || refused) return null;

  const handleCancel = async (): Promise<void> => {
    setFlash(null);
    try {
      await cancelEmailChange({});
    } catch {
      setFlash({ kind: 'cancel-failed', path: pathname });
      return;
    }
    await api.getCurrentUser.invalidateQueries();
    setFlash({ kind: 'cancelled', path: pathname });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // As in legacy, the banner hides once the pending address equals the account
  // address.
  const showPending =
    me.pending_email !== null && me.pending_email !== me.email;

  const cancelled = flash?.kind === 'cancelled';
  const cancelFailed = flash?.kind === 'cancel-failed';

  return (
    <>
      {(cancelled || cancelFailed || showPending) && (
        <NoticeArea>
          {/* Legacy flashes this when a pending change is dismissed. */}
          {cancelled && !showPending && (
            <AlertBox variant="success">
              {t('notifications.you-have-successfully-dismissed')}
            </AlertBox>
          )}
          {cancelFailed && (
            <AlertBox variant="error">
              {t('wbs.something-went-wrong-please')}
            </AlertBox>
          )}
          {showPending && me.pending_email !== null && (
            <PendingEmailNotice
              pendingEmail={me.pending_email}
              onCancel={handleCancel}
              isCancelling={isCancelling}
            />
          )}
        </NoticeArea>
      )}
      <Outlet />
    </>
  );
}

// The leaves each open with their own `PageContent`, which carries the top
// padding; a banner rendered above them needs its own or it sits flush against
// the frame.
const NoticeArea = styled.div`
  padding: 18px 4px 0 0;
`;
